import React, { useState, useEffect } from 'react';
import {
  Calendar, Plus, Download, X, Save, Search, FileSpreadsheet
} from 'lucide-react';

const ActivitesManagement = ({ anneeScolaire = '2025/2026' }) => {
  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedActivite, setSelectedActivite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');

  const OPTIONS = {
    typesActivite: [
      'Portes ouvertes',
      'Activité parascolaire',
      'Atelier pédagogique',
      'Conférence / Talk',
      'Salon interne',
      'Autre'
    ],
    objectifs: [
      'Fidéliser les étudiants actuels',
      'Augmenter la notoriété de SUP2I',
      'Attirer de nouveaux prospects',
      'Mettre en avant la reconnaissance de l\'État',
      'Montrer l\'infrastructure',
      'Créer du contenu pour les réseaux sociaux',
      'Convertir des leads en inscriptions'
    ],
    statuts: ['planifiée', 'en_cours', 'terminée', 'annulée']
  };

  const [formData, setFormData] = useState({
    intitule: '',
    typeActivite: '',
    dateActivite: '',
    lieu: '',
    responsableOperationnel: '',
    equipeImpliquee: [],
    cibles: [],
    objectifs: [],
    filieresVisees: [],
    objectifQuantitatif: {
      participantsVises: 0,
      leadsAttendus: 0,
      inscriptionsPotentielles: 0
    },
    programme: [],
    messagesClés: [],
    budget: {
      logistique: 0,
      communication: 0,
      collations: 0,
      cadeaux: 0,
      interventionExterne: 0,
      materielPedagogique: 0,
      autres: 0
    },
    contenuAProdruire: [],
    responsableContenu: '',
    equipeAudiovisuelle: '',
    statut: 'planifiée',
    anneeScolaire: anneeScolaire
  });

  useEffect(() => {
    fetchActivites();
  }, [anneeScolaire]);

  const fetchActivites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`     https://vmi1977988.contaboserver.net/api2/activites?anneeScolaire=${anneeScolaire}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setActivites(data.data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedActivite
        ? `     https://vmi1977988.contaboserver.net/api2/activites/${selectedActivite._id}`
        : '    https://vmi1977988.contaboserver.net/api2/activites';

      const method = selectedActivite ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...formData, anneeScolaire })
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message);
        setShowModal(false);
        resetForm();
        fetchActivites();
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette activité ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`     https://vmi1977988.contaboserver.net/api2/activites/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message);
        fetchActivites();
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleEdit = (activite) => {
    setSelectedActivite(activite);
    setFormData(activite);
    setShowModal(true);
  };

  const handleViewDetails = (activite) => {
    setSelectedActivite(activite);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setSelectedActivite(null);
    setFormData({
      intitule: '',
      typeActivite: '',
      dateActivite: '',
      lieu: '',
      responsableOperationnel: '',
      equipeImpliquee: [],
      cibles: [],
      objectifs: [],
      filieresVisees: [],
      objectifQuantitatif: {
        participantsVises: 0,
        leadsAttendus: 0,
        inscriptionsPotentielles: 0
      },
      programme: [],
      messagesClés: [],
      budget: {
        logistique: 0,
        communication: 0,
        collations: 0,
        cadeaux: 0,
        interventionExterne: 0,
        materielPedagogique: 0,
        autres: 0
      },
      contenuAProdruire: [],
      responsableContenu: '',
      equipeAudiovisuelle: '',
      statut: 'planifiée',
      anneeScolaire: anneeScolaire
    });
  };

  const toggleSelection = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const calculateBudgetTotal = () => {
    return Object.values(formData.budget).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const exportToExcel = () => {
    let csvContent = '\uFEFF'; // BOM pour Excel UTF-8

    // En-tête
    csvContent += `GESTION DES ACTIVITÉS SUP2I - ${anneeScolaire}\n`;
    csvContent += `Date d'export: ${new Date().toLocaleDateString('fr-FR')}\n`;
    csvContent += `Nombre total d'activités: ${filteredActivites.length}\n`;
    csvContent += '\n';

    // Statistiques
    const stats = {
      planifiée: filteredActivites.filter(a => a.statut === 'planifiée').length,
      en_cours: filteredActivites.filter(a => a.statut === 'en_cours').length,
      terminée: filteredActivites.filter(a => a.statut === 'terminée').length,
      annulée: filteredActivites.filter(a => a.statut === 'annulée').length
    };

    csvContent += 'STATISTIQUES PAR STATUT\n';
    csvContent += 'Statut,Nombre\n';
    csvContent += `Planifiées,${stats.planifiée}\n`;
    csvContent += `En cours,${stats.en_cours}\n`;
    csvContent += `Terminées,${stats.terminée}\n`;
    csvContent += `Annulées,${stats.annulée}\n`;
    csvContent += '\n';

    // Budget total
    const budgetTotal = filteredActivites.reduce((sum, a) => {
      const budget = a.budgetTotal || 0;
      return sum + budget;
    }, 0);
    csvContent += `BUDGET TOTAL: ${budgetTotal.toLocaleString('fr-FR')} MAD\n`;
    csvContent += '\n';

    // Tableau détaillé
    csvContent += 'DÉTAIL DES ACTIVITÉS\n';
    csvContent += 'N°,Intitulé,Type,Date,Lieu,Responsable,Statut,Participants Visés,Leads Attendus,Budget (MAD),Objectifs\n';

    filteredActivites.forEach((activite, index) => {
      const objectifs = (activite.objectifs || []).join(' | ');
      const budgetTotal = activite.budgetTotal || 0;
      const participantsVises = activite.objectifQuantitatif?.participantsVises || 0;
      const leadsAttendus = activite.objectifQuantitatif?.leadsAttendus || 0;

      csvContent += `${index + 1},"${activite.intitule}","${activite.typeActivite}",${new Date(activite.dateActivite).toLocaleDateString('fr-FR')},"${activite.lieu}","${activite.responsableOperationnel}",${getStatutLabel(activite.statut)},${participantsVises},${leadsAttendus},${budgetTotal.toLocaleString('fr-FR')},"${objectifs}"\n`;
    });

    // Téléchargement
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Activites_SUP2I_${anneeScolaire.replace('/', '-')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredActivites = activites.filter(act => {
    const matchSearch = act.intitule.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.lieu.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === 'tous' || act.statut === filterStatut;
    return matchSearch && matchStatut;
  });

  const getStatutColor = (statut) => {
    const colors = {
      'planifiée': '#4472C4',
      'en_cours': '#ED7D31',
      'terminée': '#70AD47',
      'annulée': '#C00000'
    };
    return colors[statut] || '#7F7F7F';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'planifiée': 'Planifiée',
      'en_cours': 'En cours',
      'terminée': 'Terminée',
      'annulée': 'Annulée'
    };
    return labels[statut] || statut;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', padding: '0', fontFamily: 'Calibri, Arial, sans-serif' }}>
      {/* En-tête style Excel */}
      <div style={{
        background: '#217346',
        color: 'white',
        padding: '1rem 1.5rem',
        borderBottom: '3px solid #1a5c37'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FileSpreadsheet size={28} strokeWidth={2.5} />
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, letterSpacing: '-0.5px' }}>
                Gestion des Activités SUP2I
              </h1>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                Année Scolaire: {anneeScolaire}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={exportToExcel}
              style={{
                background: '#107C41',
                color: 'white',
                border: '1px solid #0e6b37',
                borderRadius: '4px',
                padding: '0.625rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            >
              <Download size={18} />
              Exporter Excel
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              style={{
                background: '#0078D4',
                color: 'white',
                border: '1px solid #005a9e',
                borderRadius: '4px',
                padding: '0.625rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            >
              <Plus size={18} />
              Nouvelle Activité
            </button>
          </div>
        </div>
      </div>

      {/* Barre de filtres style Excel */}
      <div style={{
        background: '#f2f2f2',
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #d0d0d0',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, position: 'relative', maxWidth: '400px' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#605E5C' }} />
          <input
            type="text"
            placeholder="Rechercher une activité..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.5rem 0.5rem 2.25rem',
              border: '1px solid #8A8886',
              borderRadius: '2px',
              fontSize: '0.9rem',
              fontFamily: 'Calibri, Arial, sans-serif'
            }}
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          style={{
            padding: '0.5rem 2rem 0.5rem 0.75rem',
            border: '1px solid #8A8886',
            borderRadius: '2px',
            fontSize: '0.9rem',
            cursor: 'pointer',
            fontFamily: 'Calibri, Arial, sans-serif',
            background: 'white'
          }}
        >
          <option value="tous">Tous les statuts</option>
          {OPTIONS.statuts.map(statut => (
            <option key={statut} value={statut}>{getStatutLabel(statut)}</option>
          ))}
        </select>
      </div>

      {/* Tableau style Excel */}
      <div style={{ padding: '1.5rem', overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #D0D0D0',
          fontSize: '0.9rem'
        }}>
          <thead>
            <tr style={{ background: '#4472C4', color: 'white' }}>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', width: '50px' }}>
                N°
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', minWidth: '250px' }}>
                Intitulé de l'Activité
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', width: '150px' }}>
                Type
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', width: '110px' }}>
                Date
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', width: '150px' }}>
                Lieu
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', width: '150px' }}>
                Responsable
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>
                Statut
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>
                Budget (MAD)
              </th>
              <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', width: '120px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredActivites.length === 0 ? (
              <tr>
                <td colSpan="9" style={{
                  padding: '3rem',
                  textAlign: 'center',
                  color: '#605E5C',
                  border: '1px solid #D0D0D0',
                  fontStyle: 'italic'
                }}>
                  Aucune activité trouvée pour {anneeScolaire}
                </td>
              </tr>
            ) : (
              filteredActivites.map((activite, index) => {
                const budgetTotal = activite.budgetTotal || 0;
                return (
                  <tr key={activite._id} style={{ background: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9' }}>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', color: '#323130' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', fontWeight: '600', color: '#323130' }}>
                      {activite.intitule}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#605E5C' }}>
                      {activite.typeActivite}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', color: '#323130' }}>
                      {new Date(activite.dateActivite).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#605E5C' }}>
                      {activite.lieu}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#605E5C' }}>
                      {activite.responsableOperationnel}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '3px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        background: getStatutColor(activite.statut),
                        color: 'white',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {getStatutLabel(activite.statut)}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', fontWeight: '600', color: '#323130' }}>
                      {budgetTotal.toLocaleString('fr-FR')}
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleViewDetails(activite)}
                          style={{
                            background: '#70AD47',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '0.375rem 0.75rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                          title="Voir détails"
                        >
                          Voir
                        </button>
                        <button
                          onClick={() => handleEdit(activite)}
                          style={{
                            background: '#0078D4',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '0.375rem 0.75rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                          title="Modifier"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(activite._id)}
                          style={{
                            background: '#D13438',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '0.375rem 0.75rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                          title="Supprimer"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {filteredActivites.length > 0 && (
            <tfoot>
              <tr style={{ background: '#E7E6E6', fontWeight: 'bold' }}>
                <td colSpan="7" style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', color: '#323130' }}>
                  TOTAL
                </td>
                <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', fontWeight: 'bold', color: '#323130' }}>
                  {filteredActivites.reduce((sum, a) => sum + (a.budgetTotal || 0), 0).toLocaleString('fr-FR')}
                </td>
                <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0' }}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Modal Détails */}
      {showDetailModal && selectedActivite && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '4px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid #D0D0D0',
            boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
          }}>
            {/* En-tête modal */}
            <div style={{
              background: '#217346',
              color: 'white',
              padding: '1rem 1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '3px solid #1a5c37'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                Détails de l'activité
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedActivite(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '0.25rem',
                  fontSize: '1.5rem',
                  lineHeight: 1
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '2rem' }}>
              {/* Titre de l'activité */}
              <div style={{
                marginBottom: '2rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid #D0D0D0'
              }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#000',
                  marginBottom: '0.5rem'
                }}>
                  {selectedActivite.intitule}
                </h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', color: '#666' }}>
                  <span style={{ fontWeight: '600' }}>
                    {new Date(selectedActivite.dateActivite).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                  <span>•</span>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: '#f0f0f0',
                    border: '1px solid #D0D0D0',
                    borderRadius: '3px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: '#000'
                  }}>
                    {getStatutLabel(selectedActivite.statut)}
                  </span>
                </div>
              </div>

              {/* Tableau des informations */}
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #D0D0D0',
                marginBottom: '2rem'
              }}>
                <tbody>
                  <tr style={{ background: '#f9f9f9' }}>
                    <td style={{
                      padding: '0.75rem',
                      border: '1px solid #D0D0D0',
                      fontWeight: 'bold',
                      width: '30%',
                      color: '#000'
                    }}>
                      Type d'activité
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      {selectedActivite.typeActivite}
                    </td>
                  </tr>
                  <tr>
                    <td style={{
                      padding: '0.75rem',
                      border: '1px solid #D0D0D0',
                      fontWeight: 'bold',
                      color: '#000'
                    }}>
                      Lieu
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      {selectedActivite.lieu}
                    </td>
                  </tr>
                  <tr style={{ background: '#f9f9f9' }}>
                    <td style={{
                      padding: '0.75rem',
                      border: '1px solid #D0D0D0',
                      fontWeight: 'bold',
                      color: '#000'
                    }}>
                      Responsable opérationnel
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      {selectedActivite.responsableOperationnel}
                    </td>
                  </tr>
                  <tr>
                    <td style={{
                      padding: '0.75rem',
                      border: '1px solid #D0D0D0',
                      fontWeight: 'bold',
                      color: '#000'
                    }}>
                      Année scolaire
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      {selectedActivite.anneeScolaire || 'Non définie'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Objectifs quantitatifs */}
              <h4 style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '2px solid #D0D0D0'
              }}>
                Objectifs quantitatifs
              </h4>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #D0D0D0',
                marginBottom: '2rem'
              }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', color: '#000' }}>
                      Indicateur
                    </th>
                    <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', color: '#000' }}>
                      Valeur
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      Participants visés
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
                      {selectedActivite.objectifQuantitatif?.participantsVises || 0}
                    </td>
                  </tr>
                  <tr style={{ background: '#f9f9f9' }}>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      Leads attendus
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
                      {selectedActivite.objectifQuantitatif?.leadsAttendus || 0}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                      Inscriptions potentielles
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
                      {selectedActivite.objectifQuantitatif?.inscriptionsPotentielles || 0}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Budget */}
              <h4 style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: '#000',
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '2px solid #D0D0D0'
              }}>
                Budget
              </h4>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #D0D0D0',
                marginBottom: '2rem'
              }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'left', fontWeight: 'bold', color: '#000' }}>
                      Poste budgétaire
                    </th>
                    <th style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', fontWeight: 'bold', color: '#000' }}>
                      Montant (MAD)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(selectedActivite.budget || {}).map(([key, value], index) => {
                    if (value > 0) {
                      return (
                        <tr key={key} style={{ background: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                          <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333', textTransform: 'capitalize' }}>
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', color: '#000', fontWeight: '600' }}>
                            {value.toLocaleString('fr-FR')}
                          </td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                  <tr style={{ background: '#217346', color: 'white', fontWeight: 'bold' }}>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', fontSize: '1.1rem' }}>
                      TOTAL
                    </td>
                    <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', textAlign: 'right', fontSize: '1.1rem' }}>
                      {(selectedActivite.budgetTotal || 0).toLocaleString('fr-FR')}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Objectifs stratégiques */}
              {selectedActivite.objectifs && selectedActivite.objectifs.length > 0 && (
                <>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: '#000',
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #D0D0D0'
                  }}>
                    Objectifs stratégiques
                  </h4>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    border: '1px solid #D0D0D0',
                    marginBottom: '2rem'
                  }}>
                    <tbody>
                      {selectedActivite.objectifs.map((obj, index) => (
                        <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                          <td style={{ padding: '0.75rem', border: '1px solid #D0D0D0', color: '#333' }}>
                            {obj}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* Bouton fermer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #D0D0D0' }}>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedActivite(null);
                  }}
                  style={{
                    padding: '0.625rem 1.5rem',
                    border: '1px solid #D0D0D0',
                    borderRadius: '2px',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    color: '#000'
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier/Créer */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '4px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid #8A8886',
            boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
          }}>
            {/* En-tête modal */}
            <div style={{
              background: '#217346',
              color: 'white',
              padding: '1rem 1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                {selectedActivite ? 'Modifier l\'activité' : 'Nouvelle activité'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* Informations de base */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  color: '#323130',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #217346'
                }}>
                  1️⃣ Informations de base
                </h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                      Intitulé de l'événement *
                    </label>
                    <input
                      type="text"
                      value={formData.intitule}
                      onChange={(e) => setFormData({ ...formData, intitule: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #8A8886',
                        borderRadius: '2px',
                        fontSize: '0.9rem',
                        fontFamily: 'Calibri, Arial, sans-serif'
                      }}
                      placeholder="Ex: Atelier Coding – Découverte du Développement Web"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                        Type d'activité *
                      </label>
                      <select
                        value={formData.typeActivite}
                        onChange={(e) => setFormData({ ...formData, typeActivite: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #8A8886',
                          borderRadius: '2px',
                          fontSize: '0.9rem',
                          fontFamily: 'Calibri, Arial, sans-serif'
                        }}
                      >
                        <option value="">Sélectionner...</option>
                        {OPTIONS.typesActivite.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                        Date *
                      </label>
                      <input
                        type="date"
                        value={formData.dateActivite}
                        onChange={(e) => setFormData({ ...formData, dateActivite: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #8A8886',
                          borderRadius: '2px',
                          fontSize: '0.9rem',
                          fontFamily: 'Calibri, Arial, sans-serif'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                        Lieu *
                      </label>
                      <input
                        type="text"
                        value={formData.lieu}
                        onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #8A8886',
                          borderRadius: '2px',
                          fontSize: '0.9rem',
                          fontFamily: 'Calibri, Arial, sans-serif'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                        Responsable opérationnel *
                      </label>
                      <input
                        type="text"
                        value={formData.responsableOperationnel}
                        onChange={(e) => setFormData({ ...formData, responsableOperationnel: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #8A8886',
                          borderRadius: '2px',
                          fontSize: '0.9rem',
                          fontFamily: 'Calibri, Arial, sans-serif'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Objectifs */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  color: '#323130',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #217346'
                }}>
                  2️⃣ Objectifs
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                  {OPTIONS.objectifs.map(obj => (
                    <label key={obj} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.objectifs.includes(obj)}
                        onChange={() => toggleSelection('objectifs', obj)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#323130' }}>{obj}</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                      Participants visés
                    </label>
                    <input
                      type="number"
                      value={formData.objectifQuantitatif.participantsVises}
                      onChange={(e) => setFormData({
                        ...formData,
                        objectifQuantitatif: {
                          ...formData.objectifQuantitatif,
                          participantsVises: parseInt(e.target.value) || 0
                        }
                      })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #8A8886',
                        borderRadius: '2px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                      Leads attendus
                    </label>
                    <input
                      type="number"
                      value={formData.objectifQuantitatif.leadsAttendus}
                      onChange={(e) => setFormData({
                        ...formData,
                        objectifQuantitatif: {
                          ...formData.objectifQuantitatif,
                          leadsAttendus: parseInt(e.target.value) || 0
                        }
                      })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #8A8886',
                        borderRadius: '2px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                      Inscriptions potentielles
                    </label>
                    <input
                      type="number"
                      value={formData.objectifQuantitatif.inscriptionsPotentielles}
                      onChange={(e) => setFormData({
                        ...formData,
                        objectifQuantitatif: {
                          ...formData.objectifQuantitatif,
                          inscriptionsPotentielles: parseInt(e.target.value) || 0
                        }
                      })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #8A8886',
                        borderRadius: '2px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Budget */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  color: '#323130',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #217346'
                }}>
                  3️⃣ Budget
                </h3>
                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  {Object.keys(formData.budget).map(key => (
                    <div key={key}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130', textTransform: 'capitalize' }}>
                        {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      </label>
                      <input
                        type="number"
                        value={formData.budget[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          budget: {
                            ...formData.budget,
                            [key]: parseFloat(e.target.value) || 0
                          }
                        })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #8A8886',
                          borderRadius: '2px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#E7F4EE',
                  borderRadius: '2px',
                  border: '1px solid #70AD47'
                }}>
                  <strong style={{ color: '#217346', fontSize: '1rem' }}>
                    Total Budget: {calculateBudgetTotal().toLocaleString('fr-FR')} MAD
                  </strong>
                </div>
              </div>

              {/* Statut */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#323130' }}>
                  Statut
                </label>
                <select
                  value={formData.statut}
                  onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #8A8886',
                    borderRadius: '2px',
                    fontSize: '0.9rem',
                    fontFamily: 'Calibri, Arial, sans-serif'
                  }}
                >
                  {OPTIONS.statuts.map(statut => (
                    <option key={statut} value={statut}>{getStatutLabel(statut)}</option>
                  ))}
                </select>
              </div>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #EDEBE9' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '0.625rem 1.5rem',
                    border: '1px solid #8A8886',
                    borderRadius: '2px',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    color: '#323130'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  style={{
                    padding: '0.625rem 1.5rem',
                    border: 'none',
                    borderRadius: '2px',
                    background: '#217346',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Save size={16} />
                  {selectedActivite ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitesManagement;